<script setup lang="ts">
import Textarea from 'primevue/textarea';
import { useField } from 'vee-validate';
import { computed, inject, nextTick, onMounted, reactive, watch } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import type { TextareaEmits, TextareaProps } from './Textarea.vue.d';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import { Nullable } from '../ts-helpers.d';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<TextareaProps>(), {
  autoResize: true,
  maxInput: undefined,
  preventInputOnMaxLength: true,
  fieldName: 'textareaInput',
  rows: 5,
  cols: undefined,
});

const emit = defineEmits<TextareaEmits>();

onMounted(() => {
  setValidator();
});

const { locale } = useWangsVueConfig('TextArea');
const Preset = inject<Record<string, any>>('preset', {}).textarea;

const field = reactive<FieldValidation<Nullable<string>>>({
  value: undefined,
});

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

const inputPlaceholder = computed(
  () =>
    props.placeholder ??
    locale.defaultPlaceholder?.replace(
      '{label}',
      props.label?.toLowerCase() ?? '',
    ),
);

const setValidator = (): void => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName, (value: Nullable<string>) => {
        return nextTick(() => setValidatorMessage(value));
      }),
    );
  }

  // Need to re-assign field.value because Object.assign remove its value
  if (props.value ?? props.modelValue) {
    field.value = props.value?.trim() ?? props.modelValue?.trim();
  }

  if (props.invalid && props.useValidator) field.validate();
};

const setValidatorMessage = (value: Nullable<string>): string | boolean => {
  if (typeof props.validatorMessage === 'string' && props.invalid) {
    return props.validatorMessage;
  } else if (typeof props.validatorMessage !== 'string') {
    const { empty, exceed } = props.validatorMessage ?? {};
    if (!value && props.mandatory) {
      return empty ?? locale.emptyInvalidText.replace('{label}', props.label);
    } else if (
      (props.maxInput && (value?.length ?? 0) > props.maxInput) ||
      (props.maxlength && (value?.length ?? 0) > props.maxlength) // This condition will be met when preventInputOnMaxLength is false
    ) {
      return (
        exceed ??
        locale.exceedMaxLengthInvalidText.replace(
          '{maxLength}',
          (props.maxInput ?? props.maxlength).toString(),
        )
      );
    }
  }

  return true;
};

/**
 * Trim the value on blur to Fix: space character trimmed on first time input
 */
const trimFieldValue = (): void => {
  field.value = field.value?.trim();
  emit('update:modelValue', field.value);
};

watch(
  () => props.value,
  (newValue) => {
    field.value = newValue?.trim();
  },
  { once: true },
);

watch(
  () => props.modelValue,
  (value) => {
    field.value = value; // Should not trim the value, it leads unexpected behavior on space input
  },
);
</script>

<template>
  <FieldWrapper
    :info="fieldInfo"
    :label="props.label"
    :mandatory="props.mandatory"
  >
    <Textarea
      v-model="field.value"
      :auto-resize="autoResize"
      :cols="cols"
      :disabled="disabled"
      :maxlength="preventInputOnMaxLength ? maxlength : undefined"
      :placeholder="inputPlaceholder"
      :pt="{
        root: Preset.root({ props, context: { disabled, invalidInput } }),
      }"
      :rows="rows"
      @blur="trimFieldValue"
      @update:model-value="emit('update:modelValue', $event)"
    />

    <ValidatorMessage :message="field.errorMessage" />
  </FieldWrapper>
</template>
